import { pick } from 'lodash-es';
import SmartyStreetsSDK from 'smartystreets-javascript-sdk';
import { branchLiveStates } from '@ourbranch/lookups';
import * as Sentry from '@sentry/react';

import config from '../../aws-exports';

const Core = SmartyStreetsSDK.core;
const AutocompleteLookup = SmartyStreetsSDK.usAutocompletePro.Lookup;
const StreetLookup = SmartyStreetsSDK.usStreet.Lookup;
const credentials = new Core.SharedCredentials(config.smartyStreetsKey);

export default {
  client: Core.buildClient.usAutocompletePro(credentials),
  addressValidatorClient: Core.buildClient.usStreet(credentials),

  async search(value, allowedStates) {
    const lookup = new AutocompleteLookup(value);
    lookup.maxResults = 5;
    // Only set includeOnlyStates if we have states to filter by
    // When allowedStates is undefined/null, allow all US states (no filter)
    if (allowedStates?.length) {
      lookup.includeOnlyStates = allowedStates;
    }
    lookup.prefer = branchLiveStates;
    lookup.geolocate = true;
    lookup.geolocatePrecision = 'CITY';

    const { result: results } = await this.client.send(lookup);

    return results.map((result) => pick(result, ['city', 'entries', 'secondary', 'state', 'streetLine', 'zipcode']));
  },

  async validateAddress(addressIn) {
    let smartyResponse;
    try {
      const { address, address2, city, state, zip } = addressIn;
      const lookup = new StreetLookup();
      lookup.street = address;
      lookup.secondary = address2;
      lookup.city = city;
      lookup.state = state;
      lookup.zipCode = zip;
      lookup.match = 'strict';

      smartyResponse = await this.addressValidatorClient.send(lookup);

      // on autocomplete selection, our code fills out 'Apt' in the address2 field upon selection
      // so if it's just 'Apt' then it's missing the unit number
      const missingAddress2 = !address2 || !address2?.trim() || address2?.trim() === 'Apt';

      // Check if address is missing unit number via Delivery Point Validation (DPV) footnotes
      // N1 — Address is missing secondary information (apartment, suite, etc.) which IS REQUIRED for delivery
      // https://smartystreets.com/docs/cloud/us-street-api#dpv-footnotes
      const needsUnitNumber =
        missingAddress2 && smartyResponse.lookups?.[0]?.result?.[0]?.analysis?.dpvFootnotes?.includes('N1');

      return {
        results: smartyResponse.lookups,
        needsUnitNumber
      };
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          addressIn,
          smartyResponse,
          failedToValidateAddress: true
        }
      });
      return {
        results: [],
        needsUnitNumber: false
      };
    }
  }
};
